// v2.4 Mobile Tactical Sprint — single source of truth for the
// < 768 px mobile breakpoint. Every component that needs to branch on
// viewport size consumes this hook instead of scattering
// window.innerWidth reads or ad-hoc media queries.
//
// Side effect: while any consumer is mounted, the document root
// carries `data-mobile="true" | "false"` and a numeric `--mobile`
// custom property (1 on mobile, 0 otherwise) so pure-CSS rules can
// branch off the same signal, e.g.:
//   html[data-mobile="true"] .feed-row { display: none }
//   .card { padding: calc(12px + 4px * var(--mobile, 0)) }

import { useEffect, useState } from 'react';

export const MOBILE_BREAKPOINT_PX = 768;
// 2026-05-21 (feed-i006-and-breakpoint brief): iPad portrait
// (820 px standard / 834 px 11" Pro) is *above* the 767 px mobile
// threshold, so it lands in the desktop path and gets the cramped
// MarketFeedRow grid. The `isNarrow` signal catches everything from
// iPad portrait down through narrow desktop windows. iPad landscape
// (1024 px exactly) stays on the desktop layout — the cut is
// `max-width: 1023px`, deliberately exclusive of 1024.
//
// `isMobile` (< 768 px) still drives phone-only behaviours that
// would be wrong on a tablet (drawer peek heights, full-bleed
// modals, CTA sizing). `isNarrow` is the wider signal — use it for
// "this layout is too cramped" decisions like the feed-row switch.
export const NARROW_BREAKPOINT_PX = 1024;
const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`;
const NARROW_QUERY = `(max-width: ${NARROW_BREAKPOINT_PX - 1}px)`;

function readMatch(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(query).matches;
}

export interface ViewportState {
  isMobile: boolean;
  isNarrow: boolean;
}

export function useViewport(): ViewportState {
  const [isMobile, setIsMobile] = useState<boolean>(() => readMatch(MOBILE_QUERY));
  const [isNarrow, setIsNarrow] = useState<boolean>(() => readMatch(NARROW_QUERY));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mobileMql = window.matchMedia(MOBILE_QUERY);
    const narrowMql = window.matchMedia(NARROW_QUERY);
    const onMobile = (e: MediaQueryListEvent): void => setIsMobile(e.matches);
    const onNarrow = (e: MediaQueryListEvent): void => setIsNarrow(e.matches);

    // Keep state in lockstep with the MQLs on mount (handles cases
    // where the viewport changed between initial read and effect
    // mount).
    setIsMobile(mobileMql.matches);
    setIsNarrow(narrowMql.matches);

    if (typeof mobileMql.addEventListener === 'function') {
      mobileMql.addEventListener('change', onMobile);
      narrowMql.addEventListener('change', onNarrow);
      return () => {
        mobileMql.removeEventListener('change', onMobile);
        narrowMql.removeEventListener('change', onNarrow);
      };
    }
    // Safari < 14 / legacy Edge fallback.
    mobileMql.addListener(onMobile);
    narrowMql.addListener(onNarrow);
    return () => {
      mobileMql.removeListener(onMobile);
      narrowMql.removeListener(onNarrow);
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.setAttribute('data-mobile', isMobile ? 'true' : 'false');
    root.setAttribute('data-narrow', isNarrow ? 'true' : 'false');
    root.style.setProperty('--mobile', isMobile ? '1' : '0');
    root.style.setProperty('--narrow', isNarrow ? '1' : '0');
  }, [isMobile, isNarrow]);

  return { isMobile, isNarrow };
}
